/**
 * DocumentViewerPage - Standalone page for viewing a power document PDF
 * Accessible via direct URL: /app/power-inquiry/documents/:powerNo/:docId
 * Supports deep linking — unauthenticated users are redirected to login,
 * then returned here after authentication.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, ArrowLeftIcon, ArrowTopRightOnSquareIcon, ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { OathSpinner } from '../../shared/OathUI';
import { downloadDocumentPdf } from '../../../services/oathPowerDocumentService';

const isMobileDevice = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const DocumentViewerPage = () => {
  const { powerNo, docId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMemo(() => isMobileDevice(), []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (!powerNo || !docId) return;

    let cancelled = false;
    let objectUrl = null;

    const fetchPdf = async () => {
      setIsLoading(true);
      setError(null);

      const result = await downloadDocumentPdf(powerNo, docId);

      if (cancelled) return;

      if (result.success && result.data) {
        objectUrl = URL.createObjectURL(result.data);
        setPdfUrl(objectUrl);
      } else {
        setError(result.error || 'Failed to load document');
      }
      setIsLoading(false);
    };

    fetchPdf();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [powerNo, docId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-950">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <button
          onClick={() => navigate('/app/power-inquiry')}
          className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Back to Power Inquiry"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-sm font-semibold text-gray-900 dark:text-white">
            Power {powerNo} — Document
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">{docId}</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 relative">
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <OathSpinner size="lg" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading document...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-400" />
            <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
            <button
              onClick={() => navigate('/app/power-inquiry')}
              className="mt-2 px-4 py-2 text-sm text-primary dark:text-primary-light border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
            >
              Back to Power Inquiry
            </button>
          </div>
        )}

        {pdfUrl && !error && (
          isMobile ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 px-6">
              <DocumentTextIcon className="h-20 w-20 text-gray-300 dark:text-gray-600" />
              <p className="text-gray-700 dark:text-gray-300 font-medium text-center">
                PDF preview is not supported on mobile devices
              </p>
              <div className="flex flex-col gap-3 w-full max-w-xs">
                <button
                  onClick={() => window.open(pdfUrl, '_blank')}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
                >
                  <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                  Open in Browser
                </button>
                <a
                  href={pdfUrl}
                  download={`Power_${powerNo}_${docId}.pdf`}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  Download PDF
                </a>
              </div>
            </div>
          ) : (
            <iframe
              src={pdfUrl}
              title={`Power ${powerNo} - ${docId}`}
              className="w-full h-full border-0"
            />
          )
        )}
      </div>
    </div>
  );
};

export default DocumentViewerPage;
